import type { Club, Court } from '@/types/tournament.model';

/**
 * The comparison form of a sport uid. Every sport-uid match in the app goes through
 * here, so the rule lives in one place instead of being restated at each call site.
 */
export function normalizeSportUid(uid: string | null | undefined): string {
  return (uid ?? '').trim().toLowerCase();
}

/**
 * Uids of the sports a club offers, derived from its courts, in comparison form.
 *
 * Typed on the courts it reads rather than on a `Club`, so it serves both club models.
 */
export function getClubSportUids(
  courts: { sport?: { uid?: string } }[] | null | undefined,
): string[] {
  return [
    ...new Set(
      (courts ?? [])
        .map((court) => normalizeSportUid(court.sport?.uid))
        .filter((uid) => uid !== ''),
    ),
  ];
}

/**
 * Filter club courts by sport.
 * When sportUid is provided, returns only courts that match the sport (case-insensitive).
 * When sportUid is empty/null, returns all courts.
 */
export function getCourtsForSport(club: Club | null | undefined, sportUid: string | null | undefined): Court[] {
  if (!club?.courts) return [];
  const normalized = normalizeSportUid(sportUid);
  if (!normalized) return club.courts;
  return club.courts.filter(
    (c) => normalizeSportUid(c.sport?.uid) === normalized,
  );
}

/**
 * Get the count of courts available for a given sport in a club.
 */
export function getCourtsCountForSport(club: Club | null | undefined, sportUid: string | null | undefined): number {
  return getCourtsForSport(club, sportUid).length;
}
